import React from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import Typography from '@mui/material/Typography'
import Close from '@mui/icons-material/Close'
import type { RecordField } from 'components/DenseRecordSheet'
import { SAFE_AREA_BOTTOM, SHEET_MAX_WIDTH, TOUCH_MIN } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'

interface VehicleRecordSheetProps {
  open: boolean
  onClose: () => void
  title: string
  fields: RecordField[]
}

/** Bottom sheet for inventory card "all fields" — larger type on phone. */
export const VehicleRecordSheet: React.FC<VehicleRecordSheetProps> = ({
  open,
  onClose,
  title,
  fields,
}) => {
  const isMobile = useIsMobile()
  const visibleFields = fields.filter(field => !field.hidden)

  return (
    <SwipeableDrawer
      anchor='bottom'
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          maxHeight: '92dvh',
          maxWidth: { sm: SHEET_MAX_WIDTH },
          mx: { sm: 'auto' },
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          pb: SAFE_AREA_BOTTOM,
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='h6'
          component='h2'
          sx={{ flex: 1, fontSize: isMobile ? '1.1rem' : '1.05rem', lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        <IconButton aria-label='Close details' onClick={onClose} sx={{ minWidth: TOUCH_MIN, minHeight: TOUCH_MIN }}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ overflowY: 'auto', px: 2, py: 1 }}>
        {visibleFields.map((field, index) => (
          <Box
            key={`${field.label}-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(7.5rem, 38%) 1fr',
              gap: isMobile ? 0.25 : 1,
              py: isMobile ? 1.25 : 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontSize: isMobile ? '0.8125rem' : undefined, fontWeight: isMobile ? 600 : 400 }}
            >
              {field.label}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                wordBreak: 'break-word',
                textAlign: isMobile ? 'left' : 'right',
                fontSize: isMobile ? '0.9375rem' : undefined,
              }}
              component='div'
            >
              {field.value === '' || field.value == null ? '—' : field.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </SwipeableDrawer>
  )
}
